import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useAuth} from '../../../helpers/AuthContext';
import Alert from '../../Alert';
import LabelOptionsForm from './LabelOptionsForm';
import LabelOrderPicker from './LabelOrderPicker';
import LabelPrintPortal, {printSheets} from './LabelPrintPortal';
import LabelSheets, {normalizeLabelText, paginateLabels} from './LabelSheets';
import PackingSlips, {SLIP_PAGE, groupSlips} from './PackingSlips';
import useLabelQueue from './useLabelQueue';
import useLabelSettings from './useLabelSettings';
import './LabelPrinter.css';

const DEFAULT_TEMPLATE = 'Avery 5163';
const NO_ORDERS = [];

function pickTemplate(templates, preferred) {
  if (preferred && templates[preferred]) return preferred;
  if (templates[DEFAULT_TEMPLATE]) return DEFAULT_TEMPLATE;
  return Object.keys(templates)[0] || '';
}

/**
 * Composition root for the seller label printer. Owns order selection, print options, in-place
 * label edits (kept until it unmounts), and the print trigger.
 *
 * @param {{initialOrderUuids?: string[], variant?: 'page'|'modal', onClose?: Function}} props
 */
export default function LabelPrinter({initialOrderUuids = NO_ORDERS, variant = 'page', onClose}) {
  const {channel} = useAuth();
  const queue = useLabelQueue();
  const settingsState = useLabelSettings();
  const {settings} = settingsState;
  const printRoot = useRef(null);

  const [selected, setSelected] = useState(null);
  const [preselectNote, setPreselectNote] = useState('');
  const [options, setOptions] = useState(null);
  const [edits, setEdits] = useState({});
  const [printError, setPrintError] = useState('');

  // Seed the selection once the queue arrives: the deep-linked orders, else every order.
  useEffect(() => {
    if (queue.loading || queue.error || selected !== null) return;
    const wanted = new Set(initialOrderUuids);
    const preselected = queue.items.filter(item => wanted.has(item.uuid)).map(item => item.uuid);
    if (wanted.size > 0 && preselected.length > 0) {
      setSelected(new Set(preselected));
      return;
    }
    if (wanted.size > 0) {
      setPreselectNote(
        'None of the requested orders are waiting to ship, so every order is selected.',
      );
    }
    setSelected(new Set(queue.items.map(item => item.uuid)));
  }, [queue.loading, queue.error, queue.items, selected, initialOrderUuids]);

  // Seed the options once settings arrive: remembered template and return address.
  useEffect(() => {
    if (!settings || options !== null) return;
    setOptions({
      output: 'address',
      template: pickTemplate(settings.templates, settings.lastTemplate),
      skip: 0,
      returnAddress: settings.returnAddress || '',
      remember: true,
    });
  }, [settings, options]);

  const template = settings && options ? settings.templates[options.template] : null;
  const selectedItems = useMemo(
    () => (queue.items || []).filter(item => selected && selected.has(item.uuid)),
    [queue.items, selected],
  );

  const labels = useMemo(() => {
    if (!options || !template) return [];
    const withEdit = (key, text) => ({key, text: edits[key] ?? normalizeLabelText(text)});
    if (options.output === 'address') {
      return selectedItems
        .filter(item => item.labels.address)
        .map(item => withEdit(`address:${item.uuid}`, item.labels.address));
    }
    if (options.output === 'packing') {
      return selectedItems.map(item => withEdit(`packing:${item.uuid}`, item.labels.packing));
    }
    if (options.output === 'return') {
      const address = normalizeLabelText(options.returnAddress);
      if (!address) return [];
      const copies = Math.max(1, template.perPage - options.skip);
      return Array.from({length: copies}, (_, index) => withEdit(`return:${index}`, address));
    }
    return [];
  }, [options, template, selectedItems, edits]);

  const isSlip = options?.output === 'slip';
  const sheets = template && !isSlip
    ? paginateLabels({labels, skip: options.skip, perPage: template.perPage})
    : [];
  const slipCount = isSlip ? groupSlips(selectedItems).length : 0;
  const nothingToPrint = isSlip ? slipCount === 0 : labels.length === 0;
  const skippedForAddress = options?.output === 'address'
    ? selectedItems.filter(item => !item.labels.address).length
    : 0;
  const storeName = channel?.basics?.title || channel?.slug || '';

  const changeOptions = patch => {
    setOptions(current => ({...current, ...patch}));
    if (patch.returnAddress !== undefined) {
      setEdits(current => Object.fromEntries(
        Object.entries(current).filter(([key]) => !key.startsWith('return:')),
      ));
    }
  };

  const handleEdit = (key, text) => setEdits(current => ({...current, [key]: text}));

  const handlePrint = () => {
    setPrintError('');
    const changes = {};
    const address = options.returnAddress.trim();
    if (options.output === 'return' && options.remember && address
      && address !== (settings.returnAddress || '')) {
      changes.returnAddress = address;
    }
    if (!isSlip && options.template !== settings.lastTemplate) {
      changes.lastTemplate = options.template;
    }
    if (Object.keys(changes).length > 0) settingsState.save(changes);

    try {
      printSheets({
        root: printRoot.current,
        width: isSlip ? SLIP_PAGE.width : template.pWidth,
        height: isSlip ? SLIP_PAGE.height : template.pHeight,
      });
    } catch (error) {
      setPrintError(error.message || 'Printing failed');
    }
  };

  const retry = () => {
    if (queue.error) queue.reload();
    if (settingsState.error) settingsState.reload();
  };

  const wrapperClass = `label-printer label-printer--${variant}`;

  if (queue.loading || settingsState.loading) {
    return (
      <div className={wrapperClass}>
        <div className="label-printer__state" role="status">
          <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
          Loading orders to ship…
        </div>
      </div>
    );
  }

  if (queue.error || settingsState.error) {
    return (
      <div className={wrapperClass}>
        <Alert type="error" className="label-printer__state">
          <p>{queue.error || settingsState.error}</p>
          <button type="button" onClick={retry}>Try again</button>
        </Alert>
      </div>
    );
  }

  if (queue.items.length === 0) {
    return (
      <div className={wrapperClass}>
        <p className="label-printer__empty">
          No orders are waiting to ship. Paid, unshipped orders for physical items show up here.
        </p>
      </div>
    );
  }

  if (!options || selected === null || !template) {
    return <div className={wrapperClass}></div>;
  }

  const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;
  const summary = isSlip
    ? plural(slipCount, 'page')
    : `${plural(labels.length, 'label')} on ${plural(sheets.length, 'sheet')}`;

  return (
    <div className={wrapperClass}>
      {settingsState.saveError && <Alert type="error">{settingsState.saveError}</Alert>}
      {printError && <Alert type="error">{printError}</Alert>}
      {preselectNote && <Alert type="info">{preselectNote}</Alert>}

      <div className="label-printer__layout">
        <div className="label-printer__controls">
          <LabelOrderPicker
            items={queue.items}
            selected={selected}
            onChange={setSelected}
            needsAddress={options.output === 'address'}
          />
          <LabelOptionsForm
            values={options}
            templates={settings.templates}
            onChange={changeOptions}
          />

          <div className="label-printer__actions">
            <p className="label-printer__summary">{summary}</p>
            {skippedForAddress > 0 && (
              <p className="label-printer__hint">
                {skippedForAddress} selected {skippedForAddress === 1 ? 'order has' : 'orders have'}
                {' '}no address label and will not print.
              </p>
            )}
            {options.output === 'return' && !options.returnAddress.trim() && (
              <p className="label-printer__hint">Enter a return address to print return labels.</p>
            )}
            <button
              type="button"
              className="label-printer__print"
              disabled={nothingToPrint}
              onClick={handlePrint}
            >
              Print <i className="fa-solid fa-print" aria-hidden="true"></i>
            </button>
            {variant === 'modal' && onClose && (
              <button type="button" onClick={onClose}>Close</button>
            )}
            <p className="label-printer__hint">
              In the print dialog, choose "No margins" and "100% scale". Do a test print on plain
              paper first.
            </p>
          </div>
        </div>

        <div className="label-printer__preview" aria-label="Print preview">
          <p className="label-printer__hint">
            Click any label to edit its text before printing.
          </p>
          {isSlip ? (
            <PackingSlips items={selectedItems} storeName={storeName} />
          ) : (
            <LabelSheets
              labels={labels}
              template={template}
              skip={options.skip}
              editable
              onEdit={handleEdit}
            />
          )}
        </div>
      </div>

      <LabelPrintPortal rootRef={printRoot}>
        {isSlip ? (
          <PackingSlips items={selectedItems} storeName={storeName} />
        ) : (
          <LabelSheets labels={labels} template={template} skip={options.skip} />
        )}
      </LabelPrintPortal>
    </div>
  );
}
